import {
  Badge,
  Card,
  Center,
  Group,
  Image,
  Loader,
  Modal,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { IconAlertTriangle, IconCheck, IconSearch, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { num } from '../lib/format';

interface ImageRow {
  sha256: string;
  part_number: string;
  source_client_num: number;
  source_account_prefix: string | null;
  mime_type: string;
  byte_size: number;
  captured_at: string;
  is_useful: boolean | null;
  image_type: string | null;
  visible_part_number: string | null;
  visible_brand: string | null;
  has_watermark: boolean | null;
  watermark_type: string | null;
  watermark_text: string | null;
  confidence: string | null;
  notes: string | null;
  part_manufacturer: string | null;
  part_description: string | null;
}

interface ListResp {
  data: ImageRow[];
  total: number;
  page: number;
  page_size: number;
}

interface StatsResp {
  summary: {
    total_rows: number;
    unique_images: number;
    unique_parts: number;
    classified: number;
    useful: number;
    watermarked: number;
    parts_with_useful_image: number;
    parts_with_branded_only: number;
  };
  by_image_type: Array<{ image_type: string; n: number }>;
  by_confidence: Array<{ confidence: string; n: number }>;
  by_watermark_type: Array<{ watermark_type: string; n: number }>;
}

const PAGE_SIZE = 60;

const TYPE_COLOR: Record<string, string> = {
  photo: 'teal',
  diagram: 'indigo',
  label: 'orange',
  packaging: 'lime',
  screenshot: 'gray',
  logo: 'gray',
  other: 'dark',
};

const WATERMARK_COLOR: Record<string, string> = {
  branded: 'red',
  attribution: 'yellow',
  decorative: 'gray',
  none: 'gray',
};

const CONFIDENCE_COLOR: Record<string, string> = {
  high: 'teal',
  medium: 'yellow',
  low: 'red',
};

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <Paper p="sm" withBorder radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="lg" fw={700}>
        {typeof value === 'number' ? num(value) : value}
      </Text>
    </Paper>
  );
}

export default function ImagesPage() {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const [useful, setUseful] = useState<string | null>('true');
  const [imageType, setImageType] = useState<string | null>(null);
  const [watermarkType, setWatermarkType] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ImageRow | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const stats = useQuery<StatsResp>({
    queryKey: ['images', 'stats'],
    queryFn: () => api<StatsResp>('/api/images/stats'),
    staleTime: 30_000,
  });

  const list = useQuery<ListResp>({
    queryKey: ['images', 'list', { debounced, useful, imageType, watermarkType, confidence, page }],
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('page', String(page));
      sp.set('page_size', String(PAGE_SIZE));
      if (debounced) {sp.set('q', debounced);}
      if (useful) {sp.set('useful', useful);}
      if (imageType) {sp.set('image_type', imageType);}
      if (watermarkType) {sp.set('watermark_type', watermarkType);}
      if (confidence) {sp.set('confidence', confidence);}
      return api<ListResp>(`/api/images?${sp.toString()}`);
    },
    placeholderData: (prev) => prev,
  });

  const summary = stats.data?.summary;
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onCardClick = (row: ImageRow) => {
    setSelected(row);
    open();
  };

  const onFilterChange = (setter: (v: string | null) => void) => (v: string | null) => {
    setter(v);
    setPage(1);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Part Images</Title>
        {stats.isLoading && <Loader size="xs" />}
      </Group>

      {summary && (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="xs">
          <StatTile label="Total Rows" value={summary.total_rows} />
          <StatTile label="Unique Images" value={summary.unique_images} />
          <StatTile label="Unique Parts" value={summary.unique_parts} />
          <StatTile label="Classified" value={summary.classified} />
          <StatTile
            label="Useful"
            value={
              summary.classified > 0
                ? `${num(summary.useful)} (${Math.round((summary.useful / summary.classified) * 100)}%)`
                : '—'
            }
          />
          <StatTile
            label="Watermarked"
            value={
              summary.classified > 0
                ? `${num(summary.watermarked)} (${Math.round((summary.watermarked / summary.classified) * 100)}%)`
                : '—'
            }
          />
        </SimpleGrid>
      )}

      <Card withBorder padding="sm">
        <Group grow wrap="wrap">
          <TextInput
            placeholder="Search part #, brand, visible PN…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
          />
          <Select
            label={null}
            placeholder="Useful"
            data={[
              { value: 'true', label: 'Useful' },
              { value: 'false', label: 'Not useful' },
              { value: 'unclassified', label: 'Unclassified' },
            ]}
            value={useful}
            onChange={onFilterChange(setUseful)}
            clearable
          />
          <Select
            placeholder="Image type"
            data={(stats.data?.by_image_type ?? []).map((t) => ({
              value: t.image_type,
              label: `${t.image_type} (${t.n})`,
            }))}
            value={imageType}
            onChange={onFilterChange(setImageType)}
            clearable
          />
          <Select
            placeholder="Confidence"
            data={(stats.data?.by_confidence ?? []).map((t) => ({
              value: t.confidence,
              label: `${t.confidence} (${t.n})`,
            }))}
            value={confidence}
            onChange={onFilterChange(setConfidence)}
            clearable
          />
          <Select
            placeholder="Watermark"
            data={(stats.data?.by_watermark_type ?? []).map((t) => ({
              value: t.watermark_type,
              label: `${t.watermark_type} (${t.n})`,
            }))}
            value={watermarkType}
            onChange={onFilterChange(setWatermarkType)}
            clearable
          />
        </Group>
      </Card>

      <Text size="sm" c="dimmed">
        {list.isLoading ? 'Loading…' : `${num(total)} matching rows`}
      </Text>

      {list.isLoading ? (
        <Center mih={200}>
          <Loader />
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="sm">
          {list.data?.data.map((row) => (
            <Card
              key={`${row.sha256}-${row.source_client_num}`}
              withBorder
              padding="xs"
              style={{ cursor: 'pointer' }}
              onClick={() => onCardClick(row)}
            >
              <Card.Section>
                <Image
                  src={`/api/images/${row.sha256}/bytes`}
                  alt={row.part_number}
                  h={140}
                  fit="contain"
                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E"
                />
              </Card.Section>
              <Stack gap={4} mt="xs">
                <Text size="xs" fw={600} truncate>
                  {row.part_number}
                </Text>
                <Group gap={4} wrap="nowrap">
                  {row.image_type && (
                    <Badge size="xs" color={TYPE_COLOR[row.image_type] ?? 'gray'} variant="light">
                      {row.image_type}
                    </Badge>
                  )}
                  {row.is_useful === false && (
                    <Tooltip label="Rejected: not useful">
                      <Badge size="xs" color="red" variant="light" leftSection={<IconX size={10} />}>
                        reject
                      </Badge>
                    </Tooltip>
                  )}
                  {row.has_watermark && (
                    <Tooltip label={`Watermark: ${row.watermark_type ?? 'unknown'}`}>
                      <Badge
                        size="xs"
                        color={WATERMARK_COLOR[row.watermark_type ?? 'none'] ?? 'gray'}
                        variant="light"
                        leftSection={<IconAlertTriangle size={10} />}
                      >
                        wm
                      </Badge>
                    </Tooltip>
                  )}
                  {row.confidence && row.confidence !== 'high' && (
                    <Badge
                      size="xs"
                      color={CONFIDENCE_COLOR[row.confidence] ?? 'gray'}
                      variant="light"
                    >
                      {row.confidence}
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination value={page} onChange={setPage} total={totalPages} siblings={1} />
        </Group>
      )}

      <Modal opened={opened} onClose={close} title="Image details" size="lg" centered>
        {selected && (
          <Stack gap="sm">
            <Image
              src={`/api/images/${selected.sha256}/bytes`}
              alt={selected.part_number}
              fit="contain"
              mah={400}
            />
            <Group gap="xs" wrap="wrap">
              <Badge size="sm">Part: {selected.part_number}</Badge>
              <Badge size="sm" color="gray" variant="light">
                Client {selected.source_client_num}
                {selected.source_account_prefix ? ` (${selected.source_account_prefix})` : ''}
              </Badge>
              <Badge size="sm" color="gray" variant="light">
                {selected.mime_type} · {num(Math.round(selected.byte_size / 1024))}KB
              </Badge>
              {selected.image_type && (
                <Badge
                  size="sm"
                  color={TYPE_COLOR[selected.image_type] ?? 'gray'}
                  variant="light"
                >
                  {selected.image_type}
                </Badge>
              )}
              {selected.is_useful !== null &&
                (selected.is_useful ? (
                  <Badge size="sm" color="teal" leftSection={<IconCheck size={12} />}>
                    useful
                  </Badge>
                ) : (
                  <Badge size="sm" color="red" leftSection={<IconX size={12} />}>
                    not useful
                  </Badge>
                ))}
              {selected.confidence && (
                <Badge
                  size="sm"
                  color={CONFIDENCE_COLOR[selected.confidence] ?? 'gray'}
                  variant="filled"
                >
                  conf: {selected.confidence}
                </Badge>
              )}
              {selected.has_watermark && (
                <Badge
                  size="sm"
                  color={WATERMARK_COLOR[selected.watermark_type ?? 'none'] ?? 'gray'}
                  variant="filled"
                  leftSection={<IconAlertTriangle size={12} />}
                >
                  {selected.watermark_type ?? 'watermark'}
                  {selected.watermark_text ? `: ${selected.watermark_text}` : ''}
                </Badge>
              )}
            </Group>
            {(selected.visible_part_number || selected.visible_brand) && (
              <Paper p="xs" withBorder radius="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Extracted from image
                </Text>
                <Group gap="md" mt={4}>
                  {selected.visible_part_number && (
                    <Text size="sm">
                      <b>PN:</b> {selected.visible_part_number}
                    </Text>
                  )}
                  {selected.visible_brand && (
                    <Text size="sm">
                      <b>Brand:</b> {selected.visible_brand}
                    </Text>
                  )}
                </Group>
              </Paper>
            )}
            {selected.notes && (
              <Paper p="xs" withBorder radius="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Model notes
                </Text>
                <Text size="sm" mt={4}>
                  {selected.notes}
                </Text>
              </Paper>
            )}
            {(selected.part_manufacturer || selected.part_description) && (
              <Paper p="xs" withBorder radius="sm">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Master record
                </Text>
                {selected.part_manufacturer && (
                  <Text size="sm" mt={4}>
                    <b>Manufacturer:</b> {selected.part_manufacturer}
                  </Text>
                )}
                {selected.part_description && (
                  <Text size="sm">
                    <b>Description:</b> {selected.part_description}
                  </Text>
                )}
              </Paper>
            )}
            <Text size="xs" c="dimmed" ff="monospace" style={{ wordBreak: 'break-all' }}>
              {selected.sha256}
            </Text>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
